/**
 * Auth Service - Gerenciamento de autenticação
 */
import { APP_CONFIG } from '@config/constants'
import { normalizeRoleName, sanitizeText } from '../security/tokenClaims'
import ApiService from './api'

function normalizeRole(role) {
  return normalizeRoleName(role)
}

function shouldForcePasswordChange(userPayload = {}) {
  const explicitFlag = userPayload.forcePasswordChange
    ?? userPayload.mustChangePassword
    ?? userPayload.passwordChangeRequired
    ?? userPayload.primeiroLogin
    ?? userPayload.firstLogin
    ?? userPayload.senhaTemporaria
    ?? userPayload.temporaryPassword

  if (typeof explicitFlag === 'boolean') return explicitFlag

  const lastAccess = userPayload.lastAccessAt
    || userPayload.last_access_at
    || userPayload.ultimoAcesso
    || userPayload.ultimo_acesso

  const createdAt = userPayload.createdAt
    || userPayload.created_at
    || userPayload.dataCriacao
    || userPayload.data_criacao

  return Boolean(createdAt) && !lastAccess
}

function mapUserData(userPayload = {}) {
  const fallbackRoleId = userPayload.roleId ?? userPayload.role_id ?? userPayload.perfilId ?? null
  const roleIds = Array.isArray(userPayload.roleIds)
    ? userPayload.roleIds
    : (Number.isFinite(Number(fallbackRoleId)) ? [Number(fallbackRoleId)] : [])

  return {
    id: userPayload.id,
    nome: sanitizeText(userPayload.nome),
    email: sanitizeText(userPayload.email).toLowerCase(),
    role: normalizeRole(userPayload.role || userPayload.perfil || userPayload.profile),
    roleId: Number.isFinite(Number(fallbackRoleId)) ? Number(fallbackRoleId) : null,
    roleIds,
    permissions: Array.isArray(userPayload.permissions) ? userPayload.permissions : [],
    lastAccessAt: userPayload.lastAccessAt || userPayload.last_access_at || userPayload.ultimoAcesso || userPayload.ultimo_acesso || null,
    forcePasswordChange: shouldForcePasswordChange(userPayload),
  }
}

const AuthService = {
  async initAuth() {
    const currentUser = this.getCurrentUser()
    if (!currentUser?.email) {
      return { success: false, error: 'Usuário não autenticado' }
    }

    const meResult = await ApiService.getCurrentAuthenticatedUser()
    if (!meResult.success || !meResult.data) {
      return { success: false, error: meResult.error || 'Sessão inválida ou expirada' }
    }

    const userPayload = meResult.data
    const normalizedUser = {
      ...mapUserData({ ...currentUser, ...userPayload }),
      role: normalizeRole(userPayload.role || currentUser.role),
      roleId: userPayload.roleId ?? userPayload.role_id ?? currentUser.roleId ?? null,
      roleIds: Array.isArray(userPayload.roleIds) ? userPayload.roleIds : (currentUser.roleIds || []),
      permissions: Array.isArray(userPayload.permissions) ? userPayload.permissions : (currentUser.permissions || []),
    }

    this.persistSession(normalizedUser)
    return { success: true, user: normalizedUser }
  },

  persistSession(userData, accessTokenLifetimeSeconds = null) {
    localStorage.setItem(APP_CONFIG.STORAGE_KEY, JSON.stringify(userData))
    if (Number.isFinite(Number(accessTokenLifetimeSeconds)) && Number(accessTokenLifetimeSeconds) > 0) {
      localStorage.setItem(
        APP_CONFIG.ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY,
        String(Date.now() + Number(accessTokenLifetimeSeconds) * 1000),
      )
    }
  },

  getAccessTokenExpiresAt() {
    const expiresAt = Number(localStorage.getItem(APP_CONFIG.ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY))
    return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : null
  },

  shouldRefreshSoon() {
    const expiresAt = this.getAccessTokenExpiresAt()
    return expiresAt === null || expiresAt - Date.now() <= APP_CONFIG.AUTH_REFRESH_EARLY_MS
  },

  getRefreshDelayMs() {
    const expiresAt = this.getAccessTokenExpiresAt()
    if (expiresAt === null) return 0
    return Math.max(0, expiresAt - Date.now() - APP_CONFIG.AUTH_REFRESH_EARLY_MS)
  },

  getDeviceId() {
    return localStorage.getItem(APP_CONFIG.DEVICE_ID_STORAGE_KEY)
  },

  ensureDeviceId() {
    const existingDeviceId = this.getDeviceId()
    if (existingDeviceId) return existingDeviceId

    const generatedDeviceId = globalThis.crypto?.randomUUID?.()
      || `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

    localStorage.setItem(APP_CONFIG.DEVICE_ID_STORAGE_KEY, generatedDeviceId)
    return generatedDeviceId
  },

  setDeviceId(deviceId) {
    if (!deviceId) return
    localStorage.setItem(APP_CONFIG.DEVICE_ID_STORAGE_KEY, String(deviceId))
  },

  /**
   * Realiza login com email e senha.
   * A área interna pode manter fallback legado durante a transição; o portal externo o desabilita explicitamente.
   */
  async login(email, password, { allowLegacyFallback = true } = {}) {
    try {
      const sanitizedEmail = sanitizeText(email).toLowerCase()
      const sanitizedPassword = password.trim()

      const deviceId = this.ensureDeviceId()
      const authResult = await ApiService.login(sanitizedEmail, sanitizedPassword, deviceId)
      if (authResult.success && authResult.data) {
        const payload = authResult.data
        const userPayload = payload.user || payload.usuario || payload
        const payloadDeviceId = payload.deviceId || payload.DeviceId || payload.device_id

        this.setDeviceId(payloadDeviceId || deviceId)

        if (!userPayload?.email) {
          return { success: false, error: 'Resposta de autenticação inválida' }
        }

        const userData = mapUserData(userPayload)

        this.persistSession(userData, payload.accessTokenLifetimeSeconds ?? payload.AccessTokenLifetimeSeconds)
        return {
          success: true,
          user: userData,
          accessTokenLifetimeSeconds: payload.accessTokenLifetimeSeconds ?? payload.AccessTokenLifetimeSeconds,
        }
      }

      if (!allowLegacyFallback) {
        return { success: false, error: authResult.error || 'E-mail ou senha inválidos.' }
      }

      const result = await ApiService.getUsuarioByEmail(sanitizedEmail)

      if (!result.success) {
        return { success: false, error: 'Erro ao conectar com o servidor' }
      }

      if (!result.data || result.data.length === 0) {
        return { success: false, error: 'Usuário não encontrado' }
      }

      const user = result.data[0]

      if (user.senha !== sanitizedPassword) {
        return { success: false, error: 'Senha incorreta' }
      }

      if (user.ativo === false) {
        return { success: false, error: 'Conta desativada. Contate o administrador.' }
      }

      const userData = mapUserData(user)

      this.persistSession(userData)
      return { success: true, user: userData }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'Erro interno ao processar login' }
    }
  },

  async refreshSession() {
    if (this.refreshSessionPromise) return this.refreshSessionPromise

    this.refreshSessionPromise = this.refreshSessionInternal().finally(() => {
      this.refreshSessionPromise = null
    })
    return this.refreshSessionPromise
  },

  async refreshSessionInternal() {
    const currentUser = this.getCurrentUser()
    if (!currentUser?.email) {
      return { success: false, error: 'Usuário não autenticado' }
    }

    const deviceId = this.getDeviceId()
    const result = await ApiService.refreshToken({
      deviceId,
      userId: currentUser.id,
    })
    if (!result.success || !result.data) {
      const recoveredSession = await this.initAuth()
      if (recoveredSession.success && recoveredSession.user) return recoveredSession

      this.logout()
      return { success: false, error: result.error || 'Não foi possível renovar a sessão' }
    }

    const payload = result.data
    const payloadDeviceId = payload.deviceId || payload.DeviceId || payload.device_id

    this.setDeviceId(payloadDeviceId || deviceId)
    const userPayload = payload.user || payload.usuario || currentUser
    const userData = {
      ...mapUserData({ ...currentUser, ...userPayload }),
      role: normalizeRole(userPayload.role || currentUser.role),
      roleId: userPayload.roleId ?? userPayload.role_id ?? currentUser.roleId ?? null,
      roleIds: Array.isArray(userPayload.roleIds) ? userPayload.roleIds : (currentUser.roleIds || []),
      permissions: Array.isArray(userPayload.permissions) ? userPayload.permissions : (currentUser.permissions || []),
    }

    this.persistSession(userData, payload.accessTokenLifetimeSeconds ?? payload.AccessTokenLifetimeSeconds)
    return {
      success: true,
      user: userData,
      accessTokenLifetimeSeconds: payload.accessTokenLifetimeSeconds ?? payload.AccessTokenLifetimeSeconds,
    }
  },

  async changePassword({ userId, currentPassword, newPassword, confirmPassword }) {
    const payload = {
      senha: newPassword,
    }

    const result = await ApiService.changeOwnPassword(payload)

    if (!result.success) {
      return { success: false, error: result.error || 'Não foi possível alterar a senha.' }
    }

    const currentUser = this.getCurrentUser()
    if (currentUser) {
      const updatedUser = { ...currentUser, forcePasswordChange: false }
      localStorage.setItem(APP_CONFIG.STORAGE_KEY, JSON.stringify(updatedUser))
      return { success: true, user: updatedUser }
    }

    return { success: true }
  },

  logout() {
    localStorage.removeItem(APP_CONFIG.STORAGE_KEY)
    localStorage.removeItem(APP_CONFIG.DEVICE_ID_STORAGE_KEY)
    localStorage.removeItem(APP_CONFIG.ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY)
    ApiService.clearAuthToken()
  },

  getCurrentUser() {
    try {
      const data = localStorage.getItem(APP_CONFIG.STORAGE_KEY)
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  },

  isAuthenticated() {
    const user = this.getCurrentUser()
    return Boolean(user?.email)
  },

  isAdmin() {
    const user = this.getCurrentUser()
    const adminIds = APP_CONFIG.ADMIN_ROLE_IDS
    return user?.role === 'ADMIN' || (user?.roleId !== null && adminIds.includes(Number(user.roleId)))
  },

  canEditPrazo(prazo) {
    const user = this.getCurrentUser()
    if (!user) return false
    if (this.isAdmin()) return true
    if (user.role === 'LAWYER') return prazo?.advogado === user.nome
    if (user.role === 'ASSISTANT') return true
    return false
  },
}

export default AuthService
